import {
  HexColorPicker,
  Placements,
  StandardSwitch,
  useFetch,
  UseFetchOptions
} from '@brandfolder/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FunctionComponent, HTMLAttributes, useContext, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import styled from 'styled-components';
import { boolean, object, SchemaOf, string } from 'yup';

import { FormFooter } from '@components/forms';
import { AsideContext } from '@context/aside';
import { BrandguideContext } from '@context/brandguide';
import { ThemeContext } from '@context/theme';
import { ApiDatum } from '@typings/api';
import { Theme, ThemeLink } from '@typings/theme';

const schema: SchemaOf<ThemeLink> = object().shape({
  backgroundColorActive: string().defined(),
  collapsePageSections: boolean().optional(),
  color: string().defined(),
  colorHover: string().defined(),
  stickyNav: boolean().optional()
});

interface PagesSettingsProps extends HTMLAttributes<HTMLFormElement> {}

const UnstyledPagesSettings: FunctionComponent<PagesSettingsProps> = (props) => {
  const { setShowPagesSettings } = useContext(AsideContext);
  const { brandguide } = useContext(BrandguideContext);
  const { theme, setTheme } = useContext(ThemeContext);

  const [isCollapsePageSections, setIsCollapsePageSections] = useState(
    !!theme.link.collapsePageSections
  );
  const [isStickyNav, setIsStickyNav] = useState(!!theme.link.stickyNav);

  const ref = useRef('');

  const { formState, handleSubmit, reset, setValue } = useForm<ThemeLink>({
    defaultValues: {
      ...theme.link
    },
    resolver: yupResolver(schema)
  });

  const { isDirty } = formState;

  const options: UseFetchOptions = {
    fetchOnMount: false,
    headers: {
      'Content-Type': 'application/json',
      'x-brandguide-key': brandguide?.key || ''
    },
    method: 'PATCH',
    url: `/api/themes/${theme.key}`
  };

  const { error, fetch, loading, response } = useFetch<ApiDatum<Partial<Theme>>>(options);

  useEffect(() => {
    setValue('collapsePageSections', isCollapsePageSections, {
      shouldDirty: true,
      shouldTouch: true
    });
  }, [isCollapsePageSections]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setValue('stickyNav', isStickyNav, {
      shouldDirty: true,
      shouldTouch: true
    });
  }, [isStickyNav]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (response) {
      setTheme({
        ...theme,
        link: {
          ...theme.link,
          ...response.data.attributes.link
        }
      });
      toast.dismiss(ref.current);
      toast.success('Saved!');

      reset({
        ...theme.link,
        ...response.data.attributes.link
      });
    }
  }, [response]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading) {
      ref.current = toast.loading('Saving...');
    }
  }, [loading]);

  useEffect(() => {
    if (error) {
      toast.dismiss(ref.current);
      toast.error('Error saving.');
    }
  }, [error]);

  return (
    <form
      {...props}
      noValidate
      onSubmit={handleSubmit((values) => {
        const link: ThemeLink = {
          ...theme.link,
          ...values
        };

        fetch({
          ...options,
          body: {
            data: {
              attributes: {
                link
              }
            }
          }
        });
      })}
    >
      <div className="page-sections-settings-wrapper">
        <HexColorPicker
          defaultColor={theme.link.color}
          id="link-color"
          insidePopout
          labels={{
            buttonLabel: 'Open color picker',
            closeButtonLabel: 'Close',
            requiredLabel: 'Required',
            textfieldLabel: 'Menu text color'
          }}
          onChange={(color) => {
            setValue('color', color, { shouldDirty: true, shouldTouch: true });
          }}
          popoutProps={{
            placement: Placements.BottomCenter
          }}
        />
        <HexColorPicker
          defaultColor={theme.link.colorHover}
          id="color-hover"
          insidePopout
          labels={{
            buttonLabel: 'Open color hover picker',
            closeButtonLabel: 'Close',
            requiredLabel: 'Required',
            textfieldLabel: 'Menu hover color'
          }}
          onChange={(color) => {
            setValue('colorHover', color, { shouldDirty: true, shouldTouch: true });
          }}
          popoutProps={{
            placement: Placements.BottomCenter
          }}
        />
        <HexColorPicker
          defaultColor={theme.link.backgroundColorActive}
          id="active-background-color"
          insidePopout
          labels={{
            buttonLabel: 'Open active background color picker',
            closeButtonLabel: 'Close',
            requiredLabel: 'Required',
            textfieldLabel: 'Active background color'
          }}
          onChange={(color) => {
            setValue('backgroundColorActive', color, { shouldDirty: true, shouldTouch: true });
          }}
          popoutProps={{
            placement: Placements.BottomCenter
          }}
        />
        {
          <StandardSwitch
            id="sticky-nav"
            isChecked={isStickyNav}
            name="sticky-nav"
            onChange={() => {
              setIsStickyNav(!isStickyNav);
            }}
          >
            Sticky Nav
          </StandardSwitch>
        }
        {
          <StandardSwitch
            id="collapse-page-sections"
            isChecked={isCollapsePageSections}
            name="collapse-page-sections"
            onChange={() => {
              setIsCollapsePageSections(!isCollapsePageSections);
            }}
          >
            Collapse Page Sections
          </StandardSwitch>
        }
      </div>
      <FormFooter
        isDirty={isDirty}
        loading={loading}
        onCancel={() => setShowPagesSettings(false)}
      />
    </form>
  );
};

const StyledPagesSettings = styled(UnstyledPagesSettings)`
  .page-sections-settings-wrapper {
    padding: 20px 20px 28px;
  }

  .bf-textfield {
    text-transform: uppercase;
  }

  .bf-textfield__hex {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const PagesSettings: FunctionComponent<PagesSettingsProps> = (props) => {
  return <StyledPagesSettings {...props} />;
};
